import { Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from './store'
import Layout from './components/Layout'
import LoadingScreen from './components/LoadingScreen'
import ProtectedRoute from './components/ProtectedRoute'
import MyCampaignsOnePage from './pages/MyCampaignsOnePage'

// Lazy-loaded pages
const LoginPage = lazy(() => import('./pages/LoginPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const CampaignsPage = lazy(() => import('./pages/CampaignsPage'))
const EmailAccountsPage = lazy(() => import('./pages/EmailAccountsPage'))
const NewEmailAccountPage = lazy(() => import('./pages/NewEmailAccountPage'));
const EmailAccountDetailPage = lazy(() => import('./pages/EmailAccountDetailPage'))
const WarmupSettingsPage = lazy(() => import('./pages/WarmupSettingsPage'))
const MasterInboxPage = lazy(() => import('./pages/MasterInboxPage'))
const NewCampaignPage = lazy(() => import('./pages/NewCampaignPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const LeadDiscoveryPage = lazy(() => import('./pages/LeadDiscoveryPage/components/NewLeadDiscoveryPage'))
const CampaignAnalyticsPage = lazy(() => import('./pages/CampaignAnalyticsPage'))
const EditEmailPage = lazy(() => import('./pages/EditEmailPage'))
const UseCampaignPage = lazy(() => import('./pages/UseCampaignPage'))
const CampaignEmailsPage = lazy(() => import('./pages/CampaignEmailsPage'))
const EmailVerificationProgress = lazy(() => import('./pages/EmailVerificationProgress'))
const EmailSendingStatusPage = lazy(() => import('./pages/EmailSendingStatusPage'))
const CampaignStatusPage = lazy(() => import('./pages/CampaignStatusPage'))
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage/SubscriptionPage'))
const InboxKitManagerPage = lazy(() => import('./pages/InboxKitManagerPage'))

// Admin pages
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'))
const AdminCustomersPage = lazy(() => import('./pages/AdminCustomersPage'))
const AdminCustomerDetailPage = lazy(() => import('./pages/AdminCustomerDetailPage'))
const AdminCampaignsPage = lazy(() => import('./pages/AdminCampaignsPage'))
const AdminCampaignDetailPage = lazy(() => import('./pages/AdminCampaignDetailPage'))
const AdminConfigurationsPage = lazy(() => import('./pages/AdminConfigurationsPage'))

const ComingSoon = () => (
    <div className="coming-soon" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        flexDirection: 'column',
        gap: '1rem',
        textAlign: 'center',
        padding: '2rem'
    }}>
        <h1 style={{
            fontSize: '3rem',
            fontWeight: 800,
            background: 'var(--primary-gradient)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: '0.5rem'
        }}>
            Coming Soon
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '1.25rem', maxWidth: '600px' }}>
            This page is not available yet.
        </p>
        <a
            href="/dashboard"
            style={{
                marginTop: '2rem',
                padding: '0.75rem 2rem',
                background: 'var(--primary-gradient)',
                color: 'white',
                textDecoration: 'none',
                borderRadius: 'var(--radius-lg)',
                fontWeight: 600,
                display: 'inline-block'
            }}
        >
            Go to Dashboard
        </a>
    </div>
)

export function AppRoutes() {
    const { isAuthenticated } = useSelector((state: RootState) => state.auth)

    return (
        <Suspense fallback={<LoadingScreen />}>
            <Routes>
                <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <Navigate to="/dashboard" />} />

                <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/my-campaigns-old" element={<CampaignsPage />} />
                    <Route path="/my-campaigns" element={<MyCampaignsOnePage />} />
                    <Route path="/campaign/new" element={<NewCampaignPage />} />
                    <Route path="/campaign/:campaignId" element={<ComingSoon />} />
                    <Route path="/campaign/:campaignUuid/status" element={<CampaignStatusPage />} />
                    <Route path="/campaign/:campaignId/use" element={<UseCampaignPage />} />
                    <Route path="/campaign/:campaignId/verify-progress" element={<EmailVerificationProgress />} />
                    <Route path="/campaign/:campaignId/preview-leads" element={<UseCampaignPage />} />
                    <Route path="/campaign/:campaignId/verify-emails" element={<UseCampaignPage />} />
                    <Route path="/campaign/:campaignId/emails" element={<CampaignEmailsPage />} />
                    <Route path="/campaign/:campaignId/analytics" element={<CampaignAnalyticsPage />} />
                    <Route path="/campaign/:campaignId/email/:emailId/edit" element={<EditEmailPage />} />
                    <Route path="/email/sending-status/:taskId" element={<EmailSendingStatusPage />} />
                    {/* <Route path="/email_accounts">
                        <Route index element={<EmailAccountsPage />} />
                        <Route path=":accountUuid" element={<EmailAccountDetailPage />} />
                        <Route path=":accountUuid/warmup/settings" element={<WarmupSettingsPage />} />
                    </Route> */}
                    <Route path='/email_accounts'>
                        <Route index element={<NewEmailAccountPage />} />
                        <Route path=":accountUuid" element={<EmailAccountDetailPage />} />
                        <Route path=":accountUuid/warmup/settings" element={<WarmupSettingsPage />} />
                    </Route>
                    <Route path="/master_inbox" element={<MasterInboxPage />} />
                    <Route path="/lead_discovery" element={<LeadDiscoveryPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/subscription" element={<SubscriptionPage />} />
                    <Route path="/inboxkit" element={<InboxKitManagerPage />} />

                </Route>

                {/* Admin-only routes: require is_admin flag */}
                <Route element={<ProtectedRoute requireAdmin><Layout /></ProtectedRoute>}>
                    <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                    <Route path="/admin/customers" element={<AdminCustomersPage />} />
                    <Route path="/admin/customers/:customerId" element={<AdminCustomerDetailPage />} />
                    <Route path="/admin/campaigns" element={<AdminCampaignsPage />} />
                    <Route path="/admin/campaigns/:campaignId" element={<AdminCampaignDetailPage />} />
                    <Route path="/admin/configurations" element={<AdminConfigurationsPage />} />
                </Route>

                <Route path="/" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />} />
                <Route path="*" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />} />
            </Routes>
        </Suspense>
    )
}
